import {
  FoodIcon,
  GstIcon,
  ItrIcon,
  LoanIcon,
  ProjectIcon,
  RentIcon,
  ShopIcon,
  UdyamIcon,
} from '../components/service/ServiceIcons';

const COMMON_ACCEPT = '.pdf,.jpg,.jpeg,.png';
const COMMON_FILE_HELP = 'Upload PDF, JPG, or PNG files only. Maximum 2 MB per document and 3 MB total per submission.';
const MERGED_FILE_HELP = 'Upload a single merged PDF, JPG, or PNG file. Maximum 2 MB per document and 3 MB total per submission.';

const baseField = (overrides) => ({
  required: true,
  width: 'half',
  ...overrides,
});

const baseFileField = (overrides) => ({
  type: 'file',
  required: true,
  width: 'full',
  accept: COMMON_ACCEPT,
  maxFiles: 1,
  maxFileSizeMB: 2,
  helpText: COMMON_FILE_HELP,
  ...overrides,
});

const commonApplicantFields = () => ([
  baseField({
    name: 'fullName',
    label: 'Full Name',
    type: 'text',
    placeholder: 'Enter your full name',
    autoComplete: 'name',
    minLength: 3,
    maxLength: 60,
  }),
  baseField({
    name: 'email',
    label: 'Email Address',
    type: 'email',
    placeholder: 'Enter your email address',
    autoComplete: 'email',
    maxLength: 100,
  }),
  baseField({
    name: 'phone',
    label: 'Mobile Number',
    type: 'tel',
    placeholder: 'Enter your 10-digit mobile number',
    autoComplete: 'tel',
    inputMode: 'numeric',
    maxLength: 10,
    pattern: '^[6-9][0-9]{9}$',
    patternMessage: 'Enter a valid 10-digit Indian mobile number.',
  }),
  baseField({
    name: 'city',
    label: 'City',
    type: 'text',
    placeholder: 'Enter your city',
    autoComplete: 'address-level2',
    minLength: 2,
    maxLength: 50,
  }),
]);

const panField = (name = 'panNumber', label = 'PAN Number') =>
  baseField({
    name,
    label,
    type: 'text',
    placeholder: 'ABCDE1234F',
    maxLength: 10,
    transform: 'uppercase',
    pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$',
    patternMessage: 'Enter a valid PAN number in uppercase format.',
  });


const moneyField = (name, label, placeholder) =>
  baseField({
    name,
    label,
    type: 'text',
    placeholder,
    inputMode: 'numeric',
    maxLength: 12,
    pattern: '^[0-9]+$',
    patternMessage: `${label} must contain numbers only.`,
  });

export const SERVICES = [
  {
    id: 'gst',
    slug: 'gst-registration',
    Icon: GstIcon,
    title: 'GST Registration',
    description:
      'Streamline your business with hassle-free GST registration services. We handle all documentation and compliance requirements.',
    pageTitle: 'GST Registration Service Request',
    pageSubtitle:
      'Share the applicant details and upload the basic documents so the team can start your GST registration workflow.',
    highlights: [
      'Complete application review before filing',
      'Document checklist built into the form',
      'Email notification includes uploaded proofs',
    ],
    fields: [
      baseField({
        name: 'businessName',
        label: 'Business Name',
        type: 'text',
        placeholder: 'Enter your registered or proposed business name',
        width: 'full',
        minLength: 2,
        maxLength: 80,
      }),
      baseField({
        name: 'businessType',
        label: 'Business Type',
        type: 'select',
        options: ['Proprietorship', 'Partnership', 'LLP', 'Private Limited', 'Other'],
      }),
      baseField({
        name: 'businessAddress',
        label: 'Business Full Address',
        type: 'textarea',
        placeholder: 'Enter the principal place of business address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseField({
        name: 'pincode',
        label: 'Pincode',
        type: 'text',
        placeholder: 'Enter 6-digit pincode',
        inputMode: 'numeric',
        maxLength: 6,
        pattern: '^[0-9]{6}$',
        patternMessage: 'Enter a valid 6-digit pincode.',
      }),
      baseField({
        name: 'businessStartDate',
        label: 'Business Start Date',
        type: 'date',
      }),
      baseField({
        name: 'email',
        label: 'Email ID',
        type: 'email',
        placeholder: 'Enter business email address',
        autoComplete: 'email',
        maxLength: 100,
      }),
      baseField({
        name: 'phone',
        label: 'Mobile Number',
        type: 'tel',
        placeholder: 'Enter 10-digit mobile number',
        autoComplete: 'tel',
        inputMode: 'numeric',
        maxLength: 10,
        pattern: '^[6-9][0-9]{9}$',
        patternMessage: 'Enter a valid 10-digit Indian mobile number.',
      }),
      baseFileField({
        name: 'aadhaarDocument',
        label: 'Aadhaar Card Copy',
      }),
      baseFileField({
        name: 'panDocument',
        label: 'PAN Card Copy',
      }),
      baseFileField({
        name: 'photoDocument',
        label: 'Photo',
      }),
    ],
  },
  {
    id: 'itr',
    slug: 'itr-filing',
    Icon: ItrIcon,
    title: 'ITR Filing',
    description:
      'Expert income tax return filing services ensuring accuracy and timely submission for individuals and businesses.',
    pageTitle: 'ITR Filing Service Request',
    pageSubtitle:
      'Collect the taxpayer details and supporting files required for return preparation in one place.',
    highlights: [
      'Useful for individuals and business owners',
      'Built-in PAN and mobile validation',
      'Supporting tax details are sent as email attachments',
    ],
    fields: [
      baseField({
        name: 'panNumber',
        label: 'PAN Number',
        type: 'text',
        placeholder: 'ABCDE1234F',
        transform: 'uppercase',
        maxLength: 10,
        pattern: '^[A-Z]{5}[0-9]{4}[A-Z]$',
        patternMessage: 'Enter a valid PAN number in uppercase format.',
      }),
      baseField({
        name: 'fullName',
        label: 'Name',
        type: 'text',
        placeholder: 'Enter your full name',
        minLength: 3,
        maxLength: 60,
      }),
      baseField({
        name: 'address',
        label: 'Address',
        type: 'textarea',
        placeholder: 'Enter your full address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseField({
        name: 'email',
        label: 'Email',
        type: 'email',
        placeholder: 'Enter your email address',
        autoComplete: 'email',
        maxLength: 100,
      }),
      baseField({
        name: 'phone',
        label: 'Mobile Number',
        type: 'tel',
        placeholder: 'Enter 10-digit mobile number',
        autoComplete: 'tel',
        inputMode: 'numeric',
        maxLength: 10,
        pattern: '^[6-9][0-9]{9}$',
        patternMessage: 'Enter a valid 10-digit Indian mobile number.',
      }),
      baseFileField({
        name: 'aadhaarDocument',
        label: 'Aadhaar Card Copy',
      }),
      baseFileField({
        name: 'panDocument',
        label: 'PAN Card Copy',
      }),
      baseFileField({
        name: 'bankDetailDocument',
        label: 'Bank Detail Document',
      }),
      baseFileField({
        name: 'loanDetailDocument',
        label: 'Loan Detail Document',
      }),
      baseFileField({
        name: 'investmentDetailDocument',
        label: 'Investment Detail Document',
      }),
    ],
  },
  {
    id: 'shop',
    slug: 'shop-act-registration',
    Icon: ShopIcon,
    title: 'Shop Act Registration',
    description:
      'Complete assistance with shop and establishment act registration to ensure your business operates legally.',
    pageTitle: 'Shop Act Registration Request',
    pageSubtitle:
      'Capture establishment details, owner information, and the mandatory supporting documents for registration.',
    highlights: [
      'Suitable for new and existing establishments',
      'Designed around the owner and business information',
      'Documents stay attached to the service email',
    ],
    fields: [
      baseField({
        name: 'businessName',
        label: 'Business Name',
        type: 'text',
        placeholder: 'Enter the business name',
        width: 'full',
        minLength: 2,
        maxLength: 80,
      }),
      baseField({
        name: 'businessType',
        label: 'Business Type',
        type: 'select',
        options: ['Proprietorship', 'Firm', 'Partnership', 'LLP', 'Private Limited', 'Other'],
      }),
      baseField({
        name: 'requestType',
        label: 'Request Type',
        type: 'select',
        options: ['New Registration', 'Renewal', 'Correction'],
      }),
      baseField({
        name: 'businessAddress',
        label: 'Business Full Address',
        type: 'textarea',
        placeholder: 'Enter the full business address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseField({
        name: 'pincode',
        label: 'Pincode',
        type: 'text',
        placeholder: 'Enter 6-digit pincode',
        inputMode: 'numeric',
        maxLength: 6,
        pattern: '^[0-9]{6}$',
        patternMessage: 'Enter a valid 6-digit pincode.',
      }),
      baseField({
        name: 'businessStartDate',
        label: 'Business Start Date',
        type: 'date',
      }),
      baseField({
        name: 'email',
        label: 'Email ID',
        type: 'email',
        placeholder: 'Enter business email address',
        autoComplete: 'email',
        maxLength: 100,
      }),
      baseField({
        name: 'phone',
        label: 'Mobile Number',
        type: 'tel',
        placeholder: 'Enter 10-digit mobile number',
        autoComplete: 'tel',
        inputMode: 'numeric',
        maxLength: 10,
        pattern: '^[6-9][0-9]{9}$',
        patternMessage: 'Enter a valid 10-digit Indian mobile number.',
      }),
      baseField({
        name: 'employeeCount',
        label: 'Number of Employees',
        type: 'text',
        placeholder: 'Enter total employees',
        inputMode: 'numeric',
        maxLength: 3,
        pattern: '^[0-9]+$',
        patternMessage: 'Enter a valid employee count.',
      }),
      baseField({
        name: 'ownerName',
        label: 'Owner Name',
        type: 'text',
        placeholder: 'Enter owner full name',
        minLength: 3,
        maxLength: 60,
      }),
      baseField({
        name: 'ownerAddress',
        label: 'Owner Address',
        type: 'textarea',
        placeholder: 'Enter owner full address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseFileField({
        name: 'aadhaarDocument',
        label: 'Aadhaar Card Copy',
      }),
      baseFileField({
        name: 'panDocument',
        label: 'Owner PAN Card Copy',
      }),
      baseFileField({
        name: 'photoDocument',
        label: 'Owner Photo',
      }),
      baseFileField({
        name: 'signatureDocument',
        label: 'Owner Signature',
      }),
      baseFileField({
        name: 'oldShopActDocument',
        label: 'Old Shop Act Copy',
        showWhen: {
          requestType: ['Renewal', 'Correction'],
        },
      }),
      baseField({
        name: 'partnerDirectorDetails',
        label: 'Partner / Director Details',
        type: 'textarea',
        placeholder: 'Enter all partner or director names, Aadhaar, PAN, email, and mobile details.',
        width: 'full',
        minLength: 20,
        maxLength: 600,
        showWhen: {
          businessType: ['Firm', 'Partnership', 'LLP', 'Private Limited'],
        },
      }),
      baseFileField({
        name: 'partnerDirectorDocuments',
        label: 'Partner / Director Aadhaar, PAN, Photo, and Signature',
        helpText: MERGED_FILE_HELP,
        showWhen: {
          businessType: ['Firm', 'Partnership', 'LLP', 'Private Limited'],
        },
      }),
    ],
  },
  {
    id: 'rent',
    slug: 'rent-agreement-service',
    Icon: RentIcon,
    title: 'Rent Agreement Service',
    description:
      'Professional rent agreement drafting and registration services with legal compliance and documentation support.',
    pageTitle: 'Rent Agreement Service Request',
    pageSubtitle:
      'Gather landlord, tenant, and property details required to prepare a compliant rent agreement request.',
    highlights: [
      'Suitable for drafting and registration enquiries',
      'Captures property and rent terms in one form',
      'ID proofs can be uploaded with the request',
    ],
    fields: [
      ...commonApplicantFields(),
      baseField({
        name: 'landlordName',
        label: 'Landlord Name',
        type: 'text',
        placeholder: 'Enter landlord full name',
        minLength: 3,
        maxLength: 60,
      }),
      baseField({
        name: 'tenantName',
        label: 'Tenant Name',
        type: 'text',
        placeholder: 'Enter tenant full name',
        minLength: 3,
        maxLength: 60,
      }),
      moneyField('monthlyRent', 'Monthly Rent Amount', 'Enter monthly rent in INR'),
      baseField({
        name: 'agreementDuration',
        label: 'Agreement Duration (Months)',
        type: 'text',
        placeholder: 'Enter agreement duration',
        inputMode: 'numeric',
        maxLength: 3,
        pattern: '^[0-9]+$',
        patternMessage: 'Enter the duration in months.',
      }),
      baseField({
        name: 'propertyAddress',
        label: 'Property Address',
        type: 'textarea',
        placeholder: 'Enter the full property address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseFileField({
        name: 'idProofDocument',
        label: 'Identity Proof',
      }),
      baseFileField({
        name: 'propertyDocument',
        label: 'Property Proof Document',
      }),
      baseFileField({
        name: 'addressProofDocument',
        label: 'Current Address Proof',
      }),
    ],
  },
  {
    id: 'loan',
    slug: 'business-loan-enquiry',
    Icon: LoanIcon,
    title: 'Business Loan Enquiry',
    description:
      'Connect with the right financial institutions and get expert guidance on business loan applications and requirements.',
    pageTitle: 'Business Loan Enquiry',
    pageSubtitle:
      'Collect the applicant and business finance information before the team reviews the loan requirement.',
    highlights: [
      'Designed for first-pass business loan assessment',
      'Captures funding need, turnover, and business age',
      'Bank statements and PAN can travel with the email',
    ],
    fields: [
      ...commonApplicantFields(),
      baseField({
        name: 'businessName',
        label: 'Business Name',
        type: 'text',
        placeholder: 'Enter your business name',
        width: 'full',
        minLength: 2,
        maxLength: 80,
      }),
      panField(),
      moneyField('loanAmount', 'Required Loan Amount', 'Enter required loan amount in INR'),
      moneyField('annualTurnover', 'Annual Turnover', 'Enter annual turnover in INR'),
      baseField({
        name: 'yearsInBusiness',
        label: 'Years in Business',
        type: 'text',
        placeholder: 'Enter years in business',
        inputMode: 'numeric',
        maxLength: 2,
        pattern: '^[0-9]+$',
        patternMessage: 'Enter completed years in business.',
      }),
      baseFileField({
        name: 'panDocument',
        label: 'PAN Card Copy',
      }),
      baseFileField({
        name: 'bankStatement',
        label: 'Latest Bank Statement',
      }),
      baseFileField({
        name: 'businessProofDocument',
        label: 'Business Proof Document',
      }),
    ],
  },
  {
    id: 'udyam',
    slug: 'udyam-registration',
    Icon: UdyamIcon,
    title: 'Udyam Registration',
    description:
      'Register your MSME quickly with Udyam registration services - documentation and filing support included.',
    pageTitle: 'Udyam Registration Service Request',
    pageSubtitle:
      'Share MSME registration inputs and upload the core proofs needed to begin the Udyam filing process.',
    highlights: [
      'Built for MSME onboarding',
      'Supports entrepreneur identity, enterprise, and bank details',
      'Uploads are bundled with the enquiry email',
    ],
    fields: [
      baseField({
        name: 'businessName',
        label: 'Business Name',
        type: 'text',
        placeholder: 'Enter the business name',
        width: 'full',
        minLength: 2,
        maxLength: 80,
      }),
      baseField({
        name: 'businessType',
        label: 'Business Type',
        type: 'select',
        options: ['Proprietorship', 'Firm', 'Partnership', 'LLP', 'Private Limited', 'Other'],
      }),
      baseField({
        name: 'requestType',
        label: 'Request Type',
        type: 'select',
        options: ['New Registration', 'Renewal', 'Correction'],
      }),
      baseField({
        name: 'businessAddress',
        label: 'Business Full Address',
        type: 'textarea',
        placeholder: 'Enter the full business address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseField({
        name: 'pincode',
        label: 'Pincode',
        type: 'text',
        placeholder: 'Enter 6-digit pincode',
        inputMode: 'numeric',
        maxLength: 6,
        pattern: '^[0-9]{6}$',
        patternMessage: 'Enter a valid 6-digit pincode.',
      }),
      baseField({
        name: 'businessStartDate',
        label: 'Business Start Date',
        type: 'date',
      }),
      baseField({
        name: 'email',
        label: 'Email ID',
        type: 'email',
        placeholder: 'Enter business email address',
        autoComplete: 'email',
        maxLength: 100,
      }),
      baseField({
        name: 'phone',
        label: 'Mobile Number',
        type: 'tel',
        placeholder: 'Enter 10-digit mobile number',
        autoComplete: 'tel',
        inputMode: 'numeric',
        maxLength: 10,
        pattern: '^[6-9][0-9]{9}$',
        patternMessage: 'Enter a valid 10-digit Indian mobile number.',
      }),
      baseField({
        name: 'employeeCount',
        label: 'Number of Employees',
        type: 'text',
        placeholder: 'Enter total employees',
        inputMode: 'numeric',
        maxLength: 3,
        pattern: '^[0-9]+$',
        patternMessage: 'Enter a valid employee count.',
      }),
      baseField({
        name: 'ownerName',
        label: 'Owner Name',
        type: 'text',
        placeholder: 'Enter owner full name',
        minLength: 3,
        maxLength: 60,
      }),
      baseField({
        name: 'ownerAddress',
        label: 'Owner Address',
        type: 'textarea',
        placeholder: 'Enter owner full address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseField({
        name: 'bankName',
        label: 'Bank Name',
        type: 'text',
        placeholder: 'Enter bank name',
        minLength: 2,
        maxLength: 80,
      }),
      baseField({
        name: 'accountNumber',
        label: 'A/c No',
        type: 'text',
        placeholder: 'Enter bank account number',
        inputMode: 'numeric',
        minLength: 9,
        maxLength: 18,
        pattern: '^[0-9]{9,18}$',
        patternMessage: 'Enter a valid bank account number.',
      }),
      baseField({
        name: 'ifscCode',
        label: 'IFSC Code',
        type: 'text',
        placeholder: 'SBIN0001234',
        transform: 'uppercase',
        maxLength: 11,
        pattern: '^[A-Z]{4}0[A-Z0-9]{6}$',
        patternMessage: 'Enter a valid IFSC code.',
      }),
      baseFileField({
        name: 'aadhaarDocument',
        label: 'Aadhaar Card Copy',
      }),
      baseFileField({
        name: 'panDocument',
        label: 'Owner PAN Card Copy',
      }),
      baseFileField({
        name: 'photoDocument',
        label: 'Owner Photo',
      }),
      baseFileField({
        name: 'signatureDocument',
        label: 'Owner Signature',
      }),
      baseFileField({
        name: 'oldUdyamDocument',
        label: 'Old Udyam / Existing Registration Copy',
        showWhen: {
          requestType: ['Renewal', 'Correction'],
        },
      }),
      baseFileField({
        name: 'firmPanDocument',
        label: 'Firm PAN Card Copy',
        showWhen: {
          businessType: ['Firm', 'Partnership', 'LLP', 'Private Limited'],
        },
      }),
      baseField({
        name: 'partnerDirectorDetails',
        label: 'Partner / Director Details',
        type: 'textarea',
        placeholder: 'Enter one partner or director name with Aadhaar and PAN details.',
        width: 'full',
        minLength: 20,
        maxLength: 400,
        showWhen: {
          businessType: ['Firm', 'Partnership', 'LLP', 'Private Limited'],
        },
      }),
      baseFileField({
        name: 'partnerDirectorDocuments',
        label: 'Partner / Director Aadhaar and PAN',
        helpText: MERGED_FILE_HELP,
        showWhen: {
          businessType: ['Firm', 'Partnership', 'LLP', 'Private Limited'],
        },
      }),
    ],
  },
  {
    id: 'food',
    slug: 'food-licence',
    Icon: FoodIcon,
    title: 'Food Licence',
    description:
      'Obtain FSSAI/food licences for your food business with end-to-end compliance and renewal assistance.',
    pageTitle: 'Food Licence Service Request',
    pageSubtitle:
      'Collect the operator details, food business information, and the documents required for the licence request.',
    highlights: [
      'FSSAI-style lead intake in one place',
      'Business type, address, and licence documents captured upfront',
      'Core licence documents are attached automatically',
    ],
    fields: [
      baseField({
        name: 'businessName',
        label: 'Food Business Name',
        type: 'text',
        placeholder: 'Enter the food business name',
        width: 'full',
        minLength: 2,
        maxLength: 80,
      }),
      baseField({
        name: 'businessType',
        label: 'Business Type',
        type: 'select',
        options: ['Proprietorship', 'Partnership', 'Company', 'LLP', 'Private Limited', 'Other'],
      }),
      baseField({
        name: 'businessAddress',
        label: 'Business Full Address',
        type: 'textarea',
        placeholder: 'Enter the food business address',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseField({
        name: 'pincode',
        label: 'Pincode',
        type: 'text',
        placeholder: 'Enter 6-digit pincode',
        inputMode: 'numeric',
        maxLength: 6,
        pattern: '^[0-9]{6}$',
        patternMessage: 'Enter a valid 6-digit pincode.',
      }),
      baseField({
        name: 'businessStartDate',
        label: 'Business Start Date',
        type: 'date',
      }),
      baseField({
        name: 'email',
        label: 'Email ID',
        type: 'email',
        placeholder: 'Enter business email address',
        autoComplete: 'email',
        maxLength: 100,
      }),
      baseField({
        name: 'phone',
        label: 'Mobile Number',
        type: 'tel',
        placeholder: 'Enter 10-digit mobile number',
        autoComplete: 'tel',
        inputMode: 'numeric',
        maxLength: 10,
        pattern: '^[6-9][0-9]{9}$',
        patternMessage: 'Enter a valid 10-digit Indian mobile number.',
      }),
      baseFileField({
        name: 'aadhaarDocument',
        label: 'Aadhaar Card Copy',
      }),
      baseFileField({
        name: 'panDocument',
        label: 'PAN Card Copy',
      }),
      baseFileField({
        name: 'photoDocument',
        label: 'Photo',
      }),
      baseFileField({
        name: 'shopActOrUdyamDocument',
        label: 'Shop Act / Udyam Registration',
      }),
      baseFileField({
        name: 'lightBillDocument',
        label: 'Light Bill',
      }),
      baseFileField({
        name: 'rentAgreementDocument',
        label: 'Rent Agreement',
      }),
      baseField({
        name: 'partnerDirectorDetails',
        label: 'Partner / Director Details',
        type: 'textarea',
        placeholder: 'Enter all partner or director names with Aadhaar, PAN, email, and mobile details.',
        width: 'full',
        minLength: 20,
        maxLength: 600,
        showWhen: {
          businessType: ['Partnership', 'Company', 'LLP', 'Private Limited'],
        },
      }),
      baseFileField({
        name: 'partnerDirectorDocuments',
        label: 'Partner / Director Aadhaar, PAN, and Photo',
        helpText: MERGED_FILE_HELP,
        showWhen: {
          businessType: ['Partnership', 'Company', 'LLP', 'Private Limited'],
        },
      }),
    ],
  },
  {
    id: 'project-report',
    slug: 'project-report',
    Icon: ProjectIcon,
    title: 'Project Report',
    description:
      'Professional project report preparation to support loan applications, grants, and business planning.',
    pageTitle: 'Project Report Request',
    pageSubtitle:
      'Capture the project basics, funding requirement, and supporting files before drafting begins.',
    highlights: [
      'Useful for funding, grant, and planning discussions',
      'Project summary and funding ask stay structured',
      'Supporting files are included as attachments',
    ],
    fields: [
      ...commonApplicantFields(),
      baseField({
        name: 'businessName',
        label: 'Business or Project Name',
        type: 'text',
        placeholder: 'Enter the project or business name',
        width: 'full',
        minLength: 2,
        maxLength: 80,
      }),
      baseField({
        name: 'projectType',
        label: 'Project Type',
        type: 'select',
        options: ['Manufacturing', 'Trading', 'Services', 'Expansion', 'Other'],
      }),
      moneyField('projectCost', 'Estimated Project Cost', 'Enter estimated project cost in INR'),
      moneyField('fundingRequirement', 'Funding Requirement', 'Enter funding requirement in INR'),
      baseField({
        name: 'projectLocation',
        label: 'Project Location',
        type: 'textarea',
        placeholder: 'Enter the proposed project location',
        width: 'full',
        minLength: 10,
        maxLength: 240,
      }),
      baseFileField({
        name: 'panDocument',
        label: 'PAN Card Copy',
      }),
      baseFileField({
        name: 'businessProfileDocument',
        label: 'Business Profile Document',
      }),
      baseFileField({
        name: 'financialsDocument',
        label: 'Financial Statement or Supporting Document',
      }),
    ],
  },
];

export const getServiceBySlug = (slug) =>
  SERVICES.find((service) => service.slug === slug);
